const React = require('react');
const { useEffect, useState } = require('react');
const T = require('prop-types');
const { Controller, useFieldArray } = require('react-hook-form');
const useForm = require('hooks/useForm');

const { default: Classes } = require('./styles.scss');

const LoadingScreen = require('components/LoadingScreen');
const BottomButtonContainer = require('components/BottomButtonContainer');
const SurveySolvePageFormConfig = require('./formConfig');

const PreventNavigationDialog = require('components/PreventNavigationDialog');
const IsEqual = require('lodash/isEqual');
const { usePrevious } = require('../../../../../hooks/usePrevious');
const { default: Grid } = require('@mui/material/Grid');
const { default: TextField } = require('@mui/material/TextField');
const { ErrorMessage } = require('@hookform/error-message');
const { default: Typography } = require('@mui/material/Typography');
const { default: FormControlLabel } = require('@mui/material/FormControlLabel');
const { default: RadioGroup } = require('@mui/material/RadioGroup');
const { default: Radio } = require('@mui/material/Radio');
const { analyticsTemplates } = require('utils/analytics');
const { default: Dialog } = require('@mui/material/Dialog');
const { default: DialogTitle } = require('@mui/material/DialogTitle');
const { default: DialogContent } = require('@mui/material/DialogContent');
const { default: DialogContentText } = require('@mui/material/DialogContentText');
const { default: DialogActions } = require('@mui/material/DialogActions');
const { default: Button } = require('@mui/material/Button');
const { default: Checkbox } = require('@mui/material/Checkbox');

const internals = {};

const SurveySolvePage = (props) => {

    // eslint-disable-next-line no-empty-pattern
    const {
        onStart,
        currentUserId,
        onSubmit,
        survey,
        navigate,
        history
    } = props;

    const formId = 'survey-solve-form';
    const [surveyFinishDialogOpen, setSurveyFinishDialogOpen] = useState(false);
    const [formSolved, setFormSolved] = useState(false);

    const onSubmitForm = async (data) => {

        const formattedData = {
            ...data,
            questions: internals.formatQuestionsForSubmission(data.questions || [])
        };

        analyticsTemplates.buttons('submit survey answers', `surveys: survey answers surveyId: ${formattedData.id}`);
        const response = await onSubmit({
            questions: formattedData.questions,
            surveyId: formattedData.id,
            userId: currentUserId
        });

        if (response && response.payload && response.payload.result && response.payload.result.status === 'success') {
            setFormSolved(true);
            setSurveyFinishDialogOpen(true);
        }
    };

    const formConfig = SurveySolvePageFormConfig.buildFormConfig({
        onSubmit: onSubmitForm,
        formId
    });

    const {
        methods: {
            control,
            handleSubmit,
            formState: {
                errors,
                isDirty,
                isValid,
                isSubmitting,
                isSubmitSuccessful
            },
            reset
        },
        renderForm
    } = useForm(formConfig);

    const renderTextWithLinks = (text) => {

        const parts = text.split(' ');

        const urlExp = /(\b(?:https?:\/\/)?[a-z0-9\-.]+\.[a-z]{2,24}(?:\/\S*)?\b)/gi;

        const matchesUrl = (maybeUrl) => {

            // If the string has 2 dots in a row, it's not a link.
            // The urlExp regex has trouble with this condition.
            // It will match hello...world, so let's not give it a chance to!
            if (maybeUrl.includes('..')) {
                return null;
            }

            return maybeUrl.match(urlExp);
        };

        const finalString = parts.reduce((out, part, currentIndex) => {

            if (typeof part !== 'string') {
                return out.concat(part);
            }

            if (part === '<newline>') {
                if (parts.length > 1 && out.length >= 1) {
                    out[currentIndex - 1] = `${parts[currentIndex - 1]}\n `;

                    return out.concat('');
                }
            }

            part = part.concat(' ');

            const subparts = part.split(urlExp).map((url) => {

                if (!matchesUrl(url)) {
                    return url; // Not actually a url
                }

                return <a key={url} target='_blank' rel="noopener noreferrer" className={Classes.itemLink} href={internals.ensureProtocol(url)}>{url}</a>;
            });

            return out.concat(subparts);
        }, []);

        return finalString;
    };

    const prevSurveyResult = usePrevious(survey);

    useEffect(() => {

        if (survey) {
            if (!IsEqual(survey, prevSurveyResult) && !isDirty) {

                reset(survey);

                if (survey && survey.questions && survey.questions.length) {

                    analyticsTemplates.buttons('start survey', `surveys: survey start surveyId: ${survey.id}`);
                    onStart({
                        surveyId: survey.id,
                        userId: currentUserId
                    });
                }
            }
        }
    }, [survey]);

    const { fields: questionFields } = useFieldArray({
        control, // control props comes from useForm (optional: if you are using FormContext)
        name: 'questions',
        keyName: 'questionId'
    });

    const submitHandler = handleSubmit(onSubmitForm);

    return (
        <BottomButtonContainer
            btnLabel='Submit'
            formId={formId}
            disabled={isSubmitting}
        >
            <PreventNavigationDialog
                saveFunction={submitHandler}
                alertMsg={'You didn\'t finish the survey.\n Do you want to continue?'}
                when={isDirty && (!isSubmitSuccessful && !formSolved)}
                navigate={(path, state) => history.push(path, state)}
                shouldBlockNavigation={() => {

                    if ((isDirty || !isValid) && (!isSubmitSuccessful && !formSolved)) {
                        return true;
                    }

                    return false;
                }}
                pageHasErrors={!isValid}
            />
            {survey && survey.questions && survey.questions.length ? renderForm(<div className={Classes.wrapper}>
                <div className={Classes.innerWrapper}>
                    <div className={Classes.topRow}>
                        <h2>{survey.title && survey.title}</h2>
                    </div>
                    {survey.description && <p>
                        {survey.description}
                    </p>}
                    <React.Fragment>
                        <Controller
                            name='id'
                            control={control}
                            render={({ field }) => {

                                return <input type="hidden" {...field} />;
                            }}
                        />
                        <Typography variant='h6' style={{ marginBottom: 18, marginTop: 12 }}>Thank you for taking our survey!</Typography>
                        {questionFields.map((questionField, index) => {

                            return (<div key={questionField.questionId} className={Classes.questionBlock}>
                                <Controller
                                    name={`questions[${index}].id`}
                                    control={control}
                                    render={({ field }) => {

                                        return <input type="hidden" {...field} />;
                                    }}
                                />
                                <Controller
                                    name={`questions[${index}].answerType`}
                                    control={control}
                                    render={({ field }) => {

                                        return <input type="hidden" {...field} />;
                                    }}
                                />
                                {questionField && questionField.text ? <Typography className={Classes.questionText}>
                                    {`${index + 1}. `}{renderTextWithLinks(questionField.text)}
                                </Typography> : null}

                                {questionField?.answerType ? (
                                    questionField.answerType === 'select' ? (
                                        <React.Fragment>
                                            <Controller
                                                name={`questions[${index}].selectedAnswer`}
                                                control={control}
                                                render={({ field }) => {

                                                    return <RadioGroup aria-label="question answer options" className={Classes.answerGroup} {...field} value={field.value ?? ''}>
                                                        {questionField && questionField.answers && questionField.answers.map((answerItem) => {
                                                            return <FormControlLabel
                                                                key={answerItem.id}
                                                                value={`${answerItem.id}`}
                                                                control={<Radio />}
                                                                label={answerItem.text}
                                                            />;
                                                        })}
                                                    </RadioGroup>;
                                                }}
                                            />
                                            <ErrorMessage
                                                errors={errors}
                                                name={`questions[${index}].selectedAnswer`}
                                                render={({ message }) => (
                                                    <Typography sx={{
                                                        color: 'error.main'
                                                    }}>{message}</Typography>
                                                )}
                                            />
                                        </React.Fragment>
                                    ) : questionField.answerType === 'checkbox' ? (
                                        <React.Fragment>
                                            <Typography variant="caption" color="textSecondary">
                                                Check all that apply
                                            </Typography>
                                            <Controller
                                                name={`questions[${index}].checkboxAnswers`}
                                                control={control}
                                                render={({ field }) => {

                                                    const value = field.value ?? [];

                                                    return (
                                                        <Grid className={Classes.answerGroup} container spacing={1}>
                                                            {questionField?.answers?.map((answerItem) => (

                                                                <Grid item xs={12} key={answerItem.id}>
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Checkbox
                                                                                checked={value.includes(answerItem.id.toString())}
                                                                                onChange={(e) => {

                                                                                    const newValue = e.target.checked
                                                                                        ? [...value, answerItem.id.toString()]
                                                                                        : value.filter((id) => id !== answerItem.id.toString());

                                                                                    field.onChange(newValue);
                                                                                }}
                                                                            />
                                                                        }
                                                                        label={answerItem.text}
                                                                    />
                                                                </Grid>
                                                            ))}
                                                        </Grid>
                                                    );
                                                }}
                                            />
                                        </React.Fragment>
                                    ) : (
                                        <React.Fragment>
                                            <Controller
                                                name={`questions[${index}].freeInputAnswer`}
                                                control={control}
                                                render={({ field, fieldState }) => {

                                                    return <TextField
                                                        {...field}
                                                        className={Classes.filledTextField}
                                                        error={Boolean(fieldState.error)}
                                                        fullWidth={true}
                                                        label='Answer'
                                                        multiline={true}
                                                        maxRows={8}
                                                        placeholder='Enter answer'
                                                        variant='filled'
                                                    />;
                                                }}
                                            />
                                            <ErrorMessage
                                                errors={errors}
                                                name={`questions[${index}].freeInputAnswer`}
                                                render={({ message }) => (
                                                    <Typography sx={{
                                                        color: 'error.main'
                                                    }}>{message}</Typography>
                                                )}
                                            />
                                        </React.Fragment>
                                    )
                                ) : null}
                            </div>);
                        })}
                        <ErrorMessage
                            errors={errors}
                            name={`questions`}
                            render={({ message }) => (

                                <Typography sx={{
                                    color: 'error.main'
                                }}>
                                    {message}
                                </Typography>
                            )}
                        />
                    </React.Fragment>
                </div>
                <Dialog
                    open={surveyFinishDialogOpen}
                    onClose={() => {

                        setSurveyFinishDialogOpen(false);
                        navigate('/app/surveys/my-surveys');
                    }}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-message"
                >
                    <DialogTitle id="alert-dialog-title">Survey Completed</DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-message">
                            {`Thank you for finishing the ${survey.title} survey. Your answers have been recorded. We appreciate your taking the time to express your views!`}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => {

                            setSurveyFinishDialogOpen(false);
                            navigate('/app/surveys/my-surveys');

                        }} color="primary" autoFocus>
                            OK
                        </Button>
                    </DialogActions>
                </Dialog>
            </div>) : <LoadingScreen variant='content' />}
        </BottomButtonContainer>
    );
};

SurveySolvePage.propTypes = {
    goBack: T.func,
    navigate: T.func,
    onSubmit: T.func,
    onStart: T.func,
    history: T.object,
    currentUserId: T.string,
    survey: T.object
};

internals.formatQuestionForSubmission = (question = {}) => {

    const formattedQuestion = {
        ...question,
        selectedAnswer: question.selectedAnswer === 'null' ? null : question.selectedAnswer
    };

    if (question.answerType === 'checkbox') {
        formattedQuestion.checkboxAnswers = Array.isArray(question.checkboxAnswers) ? question.checkboxAnswers : [];
    }

    return formattedQuestion;
};

internals.formatQuestionsForSubmission = (questions = []) => questions.map(internals.formatQuestionForSubmission);

internals.ensureProtocol = (url) => {

    if (url.match(/^https?:\/\//i)) {
        return url;
    }

    return `http://${url}`;
};

module.exports = SurveySolvePage;
module.exports.internals = internals;
